// One resolution of "what should the nav show right now", shared by all four
// chrome formats so the panel, the bottom bar, the mobile app bar and the
// drawer can never disagree with each other.
//
// Deliberately unmemoised: `usePermissions` hands back a fresh
// `hasAnyPermission` every render, so any dependency array honest enough to
// include it would recompute anyway. Filtering a handful of nav items is
// cheaper than the bookkeeping.

import { useLocation } from 'react-router-dom';

import { openTasksLabel } from '../../components/dashboards/venues/open-tasks';
import { useVenueTaskCount } from '../../components/dashboards/venues/use-venue-task-count';
import { useOpenReviewCount } from '../../components/review/useOpenReviewCount';
import { usePermissions } from '../../hooks/usePermissions';

import { navTreeFor, sectionFromPath } from './config';
import type { NavLinkEntry, NavRailItem, NavSection, SectionId } from './config';
import type { AppModeInfo } from './use-app-mode';

/** A top-level entry as this viewer sees it: a section with the pages they may
 *  open in it, or a single link. */
export type NavModelEntry = NavModelSection | { kind: 'link'; link: NavLinkEntry };

export interface NavModelSection {
  kind: 'section';
  section: NavSection;
  items: NavRailItem[];
}

export interface NavModel {
  /** Section the current route belongs to — highlighted and opened. */
  activeSection: SectionId | null;
  entries: NavModelEntry[];
  /** The common destinations in the mobile bottom bar, around Search. */
  bottomTabs: NavRailItem[];
  /** Whether the bottom bar offers More. Below a handful of destinations the
   *  tabs already cover the menu, so More would lead nowhere new. */
  showMore: boolean;
}

/** The fewest top-level destinations at which More earns its slot. */
const MORE_MIN_ENTRIES = 5;

export function useNavModel(viewer: AppModeInfo): NavModel {
  const { pathname } = useLocation();
  const { hasAnyPermission } = usePermissions();
  // Unconditional, regardless of whether Needs review is on this section: the
  // hook declines to fetch without the read permission and swallows failures,
  // so an absent or erroring count endpoint leaves the nav exactly as it is.
  const openReviewCount = useOpenReviewCount();
  // Same shape: it declines to fetch unless the viewer has assigned regions and
  // can read venues, and a failure just leaves the badge undrawn.
  const openVenueTasks = useVenueTaskCount();
  const tree = navTreeFor(viewer.mode);

  const allowed = (item: { requiresAnyOf?: NavRailItem['requiresAnyOf'] }) =>
    !item.requiresAnyOf || hasAnyPermission(...item.requiresAnyOf);

  const withBadge = (i: NavRailItem): NavRailItem => {
    if (i.id === 'needs-review') return { ...i, badge: openReviewCount };
    if (i.id === 'venues') {
      // With tasks open the link goes straight to them. The current-page check
      // ignores the query, so the item stays marked.
      const to = openVenueTasks ? `${i.to}?tasks=open` : i.to;
      return { ...i, to, badge: openVenueTasks, badgeLabel: openTasksLabel };
    }
    return i;
  };

  const itemsOf = (section: NavSection) => section.items.filter(allowed).map(withBadge);

  // A ready section with no pages for this viewer is a section they have no
  // business in — a Finance viewer has nothing under Customers — and offering
  // the row would just link them at a page they cannot open. Unbuilt sections
  // stay, rendering an inert "Soon" row.
  const entries = tree.entries.flatMap((entry): NavModelEntry[] => {
    if (entry.kind === 'link') return allowed(entry) ? [{ kind: 'link', link: entry }] : [];
    const items = itemsOf(entry);
    if (entry.ready && items.length === 0) return [];
    return [{ kind: 'section', section: entry, items }];
  });

  return {
    activeSection: sectionFromPath(tree, pathname),
    entries,
    bottomTabs: tree.bottomTabs.filter(allowed),
    showMore: entries.length >= MORE_MIN_ENTRIES,
  };
}
